/* eslint-disable camelcase */
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import ConsentFieldRenderer from './ConsentFieldRenderer';

// Returns a responsive width pair: full-width on mobile (< sm breakpoint, 600px)
// and the authored grid-cell width from sm and up. Authored forms typically use
// columnSpan in /12 increments (e.g. Title=4 / First=4 / Last=4 or Email=6 /
// DOB=6); at native mobile widths (~390px) those squished columns are unusable
// — see prod consent forms on iPhones — so collapse to a single column there
// while leaving tablet/desktop layouts untouched. Mirrors the same collapse the
// builder's FormQuickPreviewDrawer already applies in the mobile preview frame.
function getFieldWidth(field) {
    const span = field.columnSpan || field.metadata?.style?.columnSpan;
    let desktopWidth;
    if (span) {
        if (span >= 12) {
            desktopWidth = '100%';
        } else {
            const percentage = (span / 12) * 100;
            desktopWidth = `calc(${percentage}% - ${(1 - span / 12) * 16}px)`;
        }
    } else {
        // Default patient info fields to 6-column (half width) if no span specified
        desktopWidth = 'calc(50% - 8px)';
    }
    return { xs: '0 0 100%', sm: `0 0 ${desktopWidth}` };
}

function getNestedValue(obj, path) {
    if (!obj || !path) return null;
    return path.split('.').reduce((acc, key) => {
        if (acc === null || acc === undefined) return null;
        if (Array.isArray(acc)) return acc[0]?.[key];
        return acc[key];
    }, obj);
}

const ConsentPatientInfo = ({ fields, fieldValues, onChange, errors, leadData, readOnly, theme }) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {[...fields].sort((a, b) => a.order - b.order).map((field) => {
            const mapping = field.lead_mapping || field.metadata?.lead_mapping;
            const isPreFilled = mapping && leadData && getNestedValue(leadData, mapping);
            return (
                <Box
                    key={field.id}
                    sx={{
                        flex: getFieldWidth(field),
                        minWidth: 0,
                        ...(isPreFilled ? { '& .MuiOutlinedInput-root': { bgcolor: '#f0fdf4' } } : {}),
                    }}
                >
                    <ConsentFieldRenderer
                        field={field}
                        value={fieldValues[field.id]}
                        onChange={onChange}
                        error={errors[field.id]}
                        readOnly={readOnly}
                        theme={theme}
                    />
                </Box>
            );
        })}
    </Box>
);

ConsentPatientInfo.propTypes = {
    fields: PropTypes.array.isRequired,
    fieldValues: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    errors: PropTypes.object,
    leadData: PropTypes.object,
    readOnly: PropTypes.bool,
    theme: PropTypes.object,
};

ConsentPatientInfo.defaultProps = {
    errors: {},
    leadData: null,
    readOnly: false,
    theme: null,
};

export default ConsentPatientInfo;
